import React, {useCallback, useRef} from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  VStack,
  useTheme,
  Spinner,
  Center,
} from '@chakra-ui/react';
import {TextG12, TextG14} from '../typography';
import UserListCard from './UserListCard';
import {useNavigate} from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import {useGetUserAddresses} from '../../hooks/useUserAddress';

interface UsersListModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  totalCount: number;
  fetchUsersHook: any;
  entityId: string | number;
  chatRoute?: string;
  hostUserId?: number;
}
const UsersListModal: React.FC<UsersListModalProps> = ({
  isOpen,
  onClose,
  title,
  totalCount,
  fetchUsersHook,
  entityId,
  chatRoute,
  hostUserId,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const observer = useRef<IntersectionObserver | null>(null);
  const {user} = useAuth();
  
  // Get current user's home address
  const {userAddresses} = useGetUserAddresses({userId: user?.id, addressType: 'Home'});
  const homeAddress = userAddresses?.[0];
  
  const {data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading} = fetchUsersHook(entityId);

  const users = data?.pages.flatMap((page: any) => page.data.data) || [];

  // Get current user's address coordinates
  const currentUserLat = homeAddress?.latitude ? parseFloat(homeAddress.latitude) : undefined;
  const currentUserLong = homeAddress?.longitude ? parseFloat(homeAddress.longitude) : undefined;

  // Callback ref for the last user element to trigger infinite scroll
  const lastUserRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isLoading || isFetchingNextPage) return;
      if (observer.current) observer.current.disconnect();
      
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });
      
      if (node) observer.current.observe(node);
    },
    [isLoading, isFetchingNextPage, hasNextPage, fetchNextPage]
  );

  const handleMentionClick = (userId: number, handle: string) => {
    if (!chatRoute) return;
    onClose();
    // Navigate to chat with @mention pre-populated and ensure Thread tab is active
    const chatRouteWithTab = `${chatRoute}?tab=Thread`;
    navigate(chatRouteWithTab, {
      state: {
        mentionUser: {
          id: userId,
          handle: handle,
        },
      },
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="80vh">
        <ModalHeader>
          <TextG14 fontWeight="600">{title}</TextG14>
          <TextG12 color={theme.colors.grey} fontWeight="400" mt={1}>
            {totalCount} {totalCount === 1 ? 'person' : 'people'}
          </TextG12>
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody pb={4} overflowY="auto">
          {isLoading ? (
            <Center py={10}>
              <Spinner color={theme.colors.blue} />
            </Center>
          ) : users.length === 0 ? (
            <Center py={10}>
              <TextG14 color={theme.colors.grey}>No users yet</TextG14>
            </Center>
          ) : (
            <VStack spacing={2} align="stretch">
              {users.map((user: any, index: number) => {
                const isLastUser = index === users.length - 1;
                return (
                  <Box key={user.user_id} ref={isLastUser ? lastUserRef : undefined}>
                    <UserListCard
                      user={user}
                      currentUserLat={currentUserLat}
                      currentUserLong={currentUserLong}
                      onMentionClick={chatRoute ? handleMentionClick : undefined}
                      hostUserId={hostUserId}
                    />
                  </Box>
                );
              })}
              
              {isFetchingNextPage && (
                <Center py={4}>
                  <Spinner size="sm" color={theme.colors.blue} />
                </Center>
              )}
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default UsersListModal;
